import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Donnee, Etiquette } from "@/composants/ui/etiquette";
import { useChargement } from "@/lib/chargement";
import { api, type ControleFranco, type FicheCommandeFournisseur } from "@/lib/api";
import {
  cumulCommandes,
  dateCourte,
  etatFranco,
  euros,
  tonEtatFranco,
} from "@/lib/libelles";

/**
 * Le contrôle informatif du franco, avant l'envoi (LOT 2 bis, ticket #78, décision 124).
 *
 * Il dit de combien la commande est sous ou sur le seuil en vigueur, les frais prévus en
 * dessous, et les commandes que d'autres préparent chez le même fournisseur. Il INFORME :
 * rien ici ne désactive « Envoyer », et une erreur de lecture ne bloque rien. L'état se lit
 * en toutes lettres (ADR-0024) ; la couleur de l'étiquette ne fait que le souligner. Seuil et
 * montants sont fermés à qui ne voit pas les montants : la carte ne s'affiche pas, et l'API
 * refuse. Sur une commande mêlée, le montant est celui des lignes de l'appelant.
 */
export function FrancoDeLaCommande({
  fiche,
  voitLesMontants,
}: {
  fiche: FicheCommandeFournisseur;
  voitLesMontants: boolean;
}) {
  const actif = fiche.statut === "BROUILLON" && voitLesMontants;
  // Ce que la fiche change (une ligne, un prix) change le contrôle : on le relit.
  const controle = useChargement<ControleFranco | null>(
    (signal) => (actif ? api.controleFranco(fiche.id, signal) : Promise.resolve(null)),
    [fiche.id, actif, fiche.montant_ht, fiche.nombre_lignes, fiche.lignes.length],
  );
  if (!actif) return null;

  const lu = controle.donnees;
  return (
    <Carte aria-label="Franco">
      <EnTeteCarte className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-encre">Franco</h2>
        {lu ? <Etiquette ton={tonEtatFranco(lu.etat)}>{etatFranco(lu.etat)}</Etiquette> : null}
      </EnTeteCarte>
      <CorpsCarte className="flex flex-col gap-4">
        {controle.erreur ? (
          <div role="status" className="flex flex-col items-start gap-2 text-sm text-encre">
            <p>
              Le contrôle du franco n'a pas pu être lu. Il ne bloque rien : la commande peut
              partir.
            </p>
            <Bouton type="button" taille="compact" variante="discret" onClick={controle.recharger}>
              Relire le franco
            </Bouton>
          </div>
        ) : null}
        {lu ? <Controle lu={lu} /> : null}
        <p className="text-xs text-encre-douce">
          Ce contrôle informe : il n'empêche jamais d'envoyer la commande.
        </p>
      </CorpsCarte>
    </Carte>
  );
}

function Controle({ lu }: { lu: ControleFranco }) {
  const manque = lu.etat === "SOUS_LE_FRANCO";
  return (
    <>
      <p role="status" className="text-sm font-medium text-encre">
        {lu.message}
      </p>
      {lu.seuil_franco_ht !== null ? (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-4 sm:grid-cols-4">
          <Donnee libelle="Seuil de franco">
            <span className="tabular-nums">{euros(lu.seuil_franco_ht)} HT</span>
          </Donnee>
          <Donnee libelle={lu.part_partielle ? "Montant de vos lignes" : "Montant de la commande"}>
            <span className="tabular-nums">{euros(lu.montant_ht)} HT</span>
          </Donnee>
          {lu.ecart_ht !== null ? (
            <Donnee libelle={manque ? "Il manque" : "Au-dessus du seuil"}>
              <span className="tabular-nums">{euros(lu.ecart_ht)} HT</span>
            </Donnee>
          ) : null}
          {lu.frais_prevus_ht !== null ? (
            <Donnee libelle="Frais prévus sous le seuil">
              <span className="tabular-nums">{euros(lu.frais_prevus_ht)} HT</span>
            </Donnee>
          ) : null}
        </dl>
      ) : null}
      {lu.part_partielle ? (
        <p className="text-sm text-encre-douce">
          Cette commande regroupe d'autres lignes que les vôtres : seules vos lignes comptent ici.
        </p>
      ) : null}
      {lu.cumul_commandes ? (
        <p className="text-sm text-encre-douce">
          {[
            `${cumulCommandes(lu.cumul_commandes)}.`,
            lu.valide_du
              ? `Conditions valables à partir du ${dateCourte(lu.valide_du)}${
                  lu.valide_au ? ` jusqu'au ${dateCourte(lu.valide_au)}` : ""
                }.`
              : null,
            lu.conditions_livraison ? `Livraison : ${lu.conditions_livraison}` : null,
          ]
            .filter(Boolean)
            .join(" ")}
        </p>
      ) : null}
      <section aria-label="Commandes en préparation d'autres chargés d'affaires chez ce fournisseur">
        <h3 className="mb-2 text-sm font-medium text-encre">
          Commandes en préparation d'autres chargés d'affaires chez ce fournisseur
        </h3>
        {lu.brouillons_autres.length === 0 ? (
          <p className="text-sm text-encre-douce">
            Aucune autre commande en préparation que vous voyez chez ce fournisseur.
          </p>
        ) : (
          <ul className="flex flex-col gap-1.5">
            {lu.brouillons_autres.map((autre) => (
              <li key={autre.id} className="text-sm text-encre">
                <span className="font-mono">{autre.numero}</span>
                {autre.prepare_par ? `, préparé par ${autre.prepare_par}` : ""} :{" "}
                {autre.nombre_lignes > 1 ? `${autre.nombre_lignes} lignes` : "1 ligne"},{" "}
                {autre.montant_ht === null ? "montant incomplet" : `${euros(autre.montant_ht)} HT`}{" "}
                ({autre.commandes_clients.join(", ")})
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );
}
